import React, { useState, useEffect, useMemo, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CalendarDays } from "lucide-react";
import { format } from "date-fns";

const DateFilterDropdown = ({
  dateRange,
  setDateRange,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [tempStartDate, setTempStartDate] = useState(customStartDate);
  const [tempEndDate, setTempEndDate] = useState(customEndDate);

  const today = useMemo(() => new Date(), []);
  const minStartDate = useMemo(() => {
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() - 180);
    return minDate;
  }, [today]);
  const maxEndDate = useMemo(() => {
    if (!tempStartDate) return today;
    const maxFromStartDate = new Date(tempStartDate);
    maxFromStartDate.setDate(maxFromStartDate.getDate() + 180);
    return maxFromStartDate < today ? maxFromStartDate : today;
  }, [tempStartDate, today]);
  const minEndDate = useMemo(() => {
    return tempStartDate || minStartDate;
  }, [tempStartDate, minStartDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const presetOptions = [
    { label: "Select Date ", value: "dateselection" },
    { label: "Recent", value: "recent" },
    { label: "Last 30 Days", value: "last30days" },
    { label: "Last 3 Months", value: "last3months" },
    { label: "Last 6 Months", value: "last6months" },
    { label: "All", value: "all" },
  ];

  const handlePresetSelect = (preset) => {
    setDateRange(preset);
    setCustomStartDate(null);
    setCustomEndDate(null);
    setIsOpen(false);
  };

  const handleApplyCustomDate = () => {
    if (tempStartDate && tempEndDate && tempEndDate >= tempStartDate) {
      setCustomStartDate(tempStartDate);
      setCustomEndDate(tempEndDate);
      setDateRange("custom");
      setIsOpen(false);
    }
  };

  const getDisplayText = () => {
    if (dateRange === "custom" && customStartDate && customEndDate) {
      return `${format(customStartDate, "MMM d, yyyy")} - ${format(
        customEndDate,
        "MMM d, yyyy"
      )}`;
    }
    return (
      presetOptions.find((opt) => opt.value === dateRange)?.label || "Recent"
    );
  };

  return (
    <div className="relative inline-block text-left w-full" ref={dropdownRef}>
      <button
        type="button"
        className="flex justify-between items-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        {getDisplayText()}
        <svg
          className="-mr-1 ml-2 h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="origin-top-left absolute left-0 mt-3 w-[23rem] rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="" role="menu" aria-orientation="vertical">
            {presetOptions.map((option) => (
              <button
                key={option.value}
                className={`${
                  dateRange === option.value
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-700"
                } block w-full text-left px-4 py-2 text-sm hover:bg-gray-100`}
                onClick={() => handlePresetSelect(option.value)}
              >
                {option.label}
              </button>
            ))}

            <hr className=" border-gray-200" />

            <div className="px-4 py-2 text-sm text-gray-500 font-medium">
              Custom period:
            </div>

            <div className="px-4 py-2 space-y-2">
              <div className="flex items-center space-x-3">
                <div className="relative w-[10rem]">
                  <DatePicker
                    selected={tempStartDate}
                    onChange={(date) => {
                      setTempStartDate(date);

                      if (!date) {
                        setTempEndDate(null);
                        return;
                      }

                      if (tempEndDate && tempEndDate < date) {
                        setTempEndDate(null);
                        return;
                      }

                      const maxFromStart = new Date(date);
                      maxFromStart.setDate(maxFromStart.getDate() + 180);
                      const upperBound =
                        maxFromStart < today ? maxFromStart : today;

                      if (tempEndDate && tempEndDate > upperBound) {
                        setTempEndDate(null);
                      }
                    }}
                    selectsStart
                    startDate={tempStartDate}
                    endDate={tempEndDate}
                    dateFormat={"dd/MM/yyyy"}
                    className="w-full border border-gray-300 rounded pl-3 pr-12 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholderText="Start date"
                    minDate={minStartDate}
                    maxDate={today}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    yearDropdownItemNumber={15}
                    scrollableYearDropdown
                  />
                  <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
                </div>

                <div className="relative w-[10rem]">
                  <DatePicker
                    selected={tempEndDate}
                    onChange={(date) => setTempEndDate(date)}
                    selectsEnd
                    startDate={tempStartDate}
                    endDate={tempEndDate}
                    dateFormat={"dd/MM/yyyy"}
                    minDate={minEndDate}
                    maxDate={maxEndDate}
                    className="w-full border border-gray-300 rounded pl-12 pr-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholderText="End date"
                    disabled={!tempStartDate}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    yearDropdownItemNumber={15}
                    scrollableYearDropdown
                  />
                  <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
                </div>
              </div>
              {!tempStartDate ? (
                <div className="text-xs text-gray-500">
                  Select a start date between{" "}
                  {format(minStartDate, "MMM d, yyyy")} and{" "}
                  {format(today, "MMM d, yyyy")}
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  End date must be between{" "}
                  {format(tempStartDate, "MMM d, yyyy")} and{" "}
                  {format(today, "MMM d, yyyy")}
                </div>
              )}

              <button
                onClick={handleApplyCustomDate}
                disabled={
                  !tempStartDate || !tempEndDate || tempEndDate < tempStartDate
                }
                className="w-full bg-[#3538CD] text-white font-medium py-2 px-4 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed mt-3"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilterDropdown;
